import { stripBase } from "./base-path";

export const MEETUP_CITIES = [
  "Adelaide",
  "Auckland",
  "Brisbane",
  "Canberra",
  "Christchurch",
  "Hobart",
  "Melbourne",
  "Perth",
  "Sydney",
  "Wellington",
] as const;

export type MeetupCity = (typeof MEETUP_CITIES)[number];

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/i;

export function isMeetupSlug(slug: string): boolean {
  const s = slug.trim();
  return SLUG_RE.test(s) && s.length <= 64;
}

export function meetupPath(slug: string): string {
  return `/meetup/${encodeURIComponent(slug.trim())}`;
}

/** Meetup slug from `/meetup/:slug` or `?meetup=`. Not conference `?event=`. */
export function meetupSlugFromLocation(pathname: string, search: string): string | null {
  const pathMatch = stripBase(pathname).match(/^\/meetup\/([^/]+)\/?$/);
  if (pathMatch?.[1] && isMeetupSlug(decodeURIComponent(pathMatch[1]))) {
    return decodeURIComponent(pathMatch[1]);
  }
  const q = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const raw = q.get("meetup")?.trim() ?? "";
  if (raw && isMeetupSlug(raw)) return raw;
  return null;
}

export type MeetupNight = {
  slug: string;
  title: string;
  city: string;
  starts_at: string;
  event_date: string;
  event_time?: string;
  location?: string | null;
  going: number;
  remaining?: number | null;
  max_capacity?: number | null;
  mine?: { status: string; short_code?: string } | null;
};

export type MeetupPhase = "upcoming" | "started" | "ended";

/** Upcoming until start; Started on the UTC calendar day of the night; Ended after that day. */
export function meetupPhase(night: Pick<MeetupNight, "starts_at">, now: Date = new Date()): MeetupPhase {
  const start = Date.parse(night.starts_at);
  if (Number.isNaN(start)) return "upcoming";
  if (now.getTime() < start) return "upcoming";
  const startDay = new Date(start).toISOString().slice(0, 10);
  const nowDay = now.toISOString().slice(0, 10);
  if (nowDay === startDay) return "started";
  return "ended";
}

export function isMeetupPast(night: MeetupNight, now: Date = new Date()): boolean {
  return meetupPhase(night, now) !== "upcoming";
}

export function isMeetupEnded(night: MeetupNight, now: Date = new Date()): boolean {
  return meetupPhase(night, now) === "ended";
}

export function isMeetupFull(night: MeetupNight): boolean {
  return typeof night.remaining === "number" && night.remaining <= 0;
}

export function meetupListStatus(night: MeetupNight, now: Date = new Date()): string {
  const phase = meetupPhase(night, now);
  if (phase === "ended") return "Ended";
  if (night.mine?.status === "going") return "Going";
  if (phase === "started") return "Started";
  if (isMeetupFull(night)) return "Full";
  return "Open";
}

/** Date, plus a clock only when the catalog has a start time. */
export function formatMeetupWhen(
  night: Pick<MeetupNight, "starts_at" | "event_time">,
  style: "short" | "long" = "short",
): string {
  const start = new Date(night.starts_at);
  if (Number.isNaN(start.getTime())) return "";
  const datePart = start.toLocaleDateString("en-AU", {
    weekday: style === "long" ? "long" : "short",
    day: "numeric",
    month: style === "long" ? "long" : "short",
    timeZone: "UTC",
  });
  if (!night.event_time?.trim()) return datePart;
  const timePart = start.toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  });
  return `${datePart}, ${timePart}`;
}

/** Home city first, then date. Does not hide other cities. */
export function sortMeetupNights(nights: MeetupNight[], homeCity: string): MeetupNight[] {
  const home = homeCity.trim().toLowerCase();
  return [...nights].sort((a, b) => {
    const aHome = home && a.city.toLowerCase() === home ? 0 : 1;
    const bHome = home && b.city.toLowerCase() === home ? 0 : 1;
    if (aHome !== bHome) return aHome - bHome;
    return a.starts_at.localeCompare(b.starts_at);
  });
}

export type MeetupTabId = "meetups" | "directory" | "opportunities" | "connections" | "me";

/** Meetup-only chrome: Meetups + Me. Pass holders keep Directory and the rest. */
export function tabsForPass(everClaimedTicket: boolean): MeetupTabId[] {
  if (!everClaimedTicket) return ["meetups", "me"];
  return ["meetups", "directory", "opportunities", "connections", "me"];
}

/** Door QR is the Meetup Registration token. Never a ticketing barcode. */
export function doorQrPayload(reg: { qr_token?: string; barcode?: string }): string | undefined {
  return reg.qr_token;
}
